import React, { useId, useMemo } from 'react';
import { cn } from '../../lib/utils.js';

export const ANGLE_UNDERLINE_PATTERN_WIDTH = 18;
export const ANGLE_UNDERLINE_PATTERN_HEIGHT = 16;
export const ANGLE_UNDERLINE_PATH = 'M0 12 L9 4 L18 12';

const ANGLE_UNDERLINE_SIZES = {
  default: {
    patternWidth: ANGLE_UNDERLINE_PATTERN_WIDTH,
    patternHeight: ANGLE_UNDERLINE_PATTERN_HEIGHT,
    path: ANGLE_UNDERLINE_PATH,
    strokeWidth: 4,
  },
  thin: {
    patternWidth: 12,
    patternHeight: 8,
    path: 'M0 6 L6 2 L12 6',
    strokeWidth: 1.5,
  },
} as const;

export type AngleUnderlineSize = keyof typeof ANGLE_UNDERLINE_SIZES;

export function getAngleUnderlineBackgroundImage(stroke = '#5ECC80'): string {
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" `,
    `width="${ANGLE_UNDERLINE_PATTERN_WIDTH}" height="${ANGLE_UNDERLINE_PATTERN_HEIGHT}" `,
    `shape-rendering="crispEdges">`,
    `<path d="${ANGLE_UNDERLINE_PATH}" fill="none" stroke="${stroke}" `,
    `stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`,
    `</svg>`,
  ].join('');

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

interface AngleUnderlineProps {
  className?: string;
  stroke?: string;
  size?: AngleUnderlineSize;
}

export const AngleUnderline: React.FC<AngleUnderlineProps> = ({
  className,
  stroke = 'var(--scribane-green)',
  size = 'default',
}) => {
  const reactId = useId();
  const patternId = useMemo(
    () => `angle-underline-${reactId.replace(/:/g, '')}`,
    [reactId],
  );
  const pattern = ANGLE_UNDERLINE_SIZES[size];

  return (
    <span
      aria-hidden="true"
      className={cn('pointer-events-none absolute overflow-hidden', className)}
    >
      <svg className="h-full w-full" shapeRendering="crispEdges">
        <defs>
          <pattern
            id={patternId}
            patternUnits="userSpaceOnUse"
            width={pattern.patternWidth}
            height={pattern.patternHeight}
          >
            <path
              d={pattern.path}
              fill="none"
              stroke={stroke}
              strokeWidth={pattern.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
    </span>
  );
};
